#!/usr/bin/env node

import fs from 'fs/promises';
import path from 'path';

import { CoderuleClients } from '@coderule/clients';

import { resolveConfig } from './config/Configurator';

import { runInitialSync, runService } from './index';

type RunMode = 'service' | 'initial';

interface CliOptions {
  token: string;
  rootPath?: string;
  mode: RunMode;
  clean: boolean;
  inlineHasher: boolean;
  dumpBush?: string;
  dumpSkill: boolean;
  env: Record<string, string>;
}

const ENV_FLAG_MAP: Record<string, string> = {
  'data-dir': 'CODERULE_DATA_DIR',
  'auth-url': 'CODERULE_AUTH_URL',
  'sync-url': 'CODERULE_SYNC_URL',
  'ast-url': 'CODERULE_AST_URL',
  'retrieval-url': 'CODERULE_RETRIEVAL_URL',
  'http-timeout': 'CODERULE_HTTP_TIMEOUT',
  'snapshot-debounce': 'CODERULE_SNAPSHOT_DEBOUNCE_MS',
  'heartbeat-interval': 'CODERULE_HEARTBEAT_INTERVAL_MS',
  'heartbeat-check': 'CODERULE_HEARTBEAT_CHECK_INTERVAL_MS',
  'queue-poll': 'CODERULE_QUEUE_POLL_INTERVAL_MS',
  'hash-batch': 'CODERULE_HASH_BATCH_SIZE',
  'hash-lease': 'CODERULE_HASH_LEASE_MS',
  'max-snapshot-attempts': 'CODERULE_MAX_SNAPSHOT_ATTEMPTS',
  'upload-chunk-size': 'CODERULE_UPLOAD_CHUNK_SIZE',
  'max-wait-time': 'CODERULE_MAX_WAIT_TIME',
};

function printUsage(): void {
  console.log(`Usage: coderule-scanner [token] [options]

Options:
  --initial, --once          Run a single initial sync then exit
  --service                  Run continuous service (default)
  --clean, --reindex         Remove existing local state before running
  --inline-hasher            Force inline hashing (debug only)
  --root <path>              Override CODERULE_ROOT
  --data-dir <path>          Override CODERULE_DATA_DIR
  --auth-url <url>           Override CODERULE_AUTH_URL
  --sync-url <url>           Override CODERULE_SYNC_URL
  --ast-url <url>            Override CODERULE_AST_URL
  --retrieval-url <url>      Override CODERULE_RETRIEVAL_URL
  --http-timeout <ms>        Override CODERULE_HTTP_TIMEOUT
  --snapshot-debounce <ms>   Override CODERULE_SNAPSHOT_DEBOUNCE_MS
  --heartbeat-interval <ms>  Override CODERULE_HEARTBEAT_INTERVAL_MS
  --heartbeat-check <ms>     Override CODERULE_HEARTBEAT_CHECK_INTERVAL_MS
  --queue-poll <ms>          Override CODERULE_QUEUE_POLL_INTERVAL_MS
  --hash-batch <n>           Override CODERULE_HASH_BATCH_SIZE
  --hash-lease <ms>          Override CODERULE_HASH_LEASE_MS
  --max-snapshot-attempts <n> Override CODERULE_MAX_SNAPSHOT_ATTEMPTS
  --upload-chunk-size <n>    Override CODERULE_UPLOAD_CHUNK_SIZE (default 1)
  --max-wait-time <sec>      Override CODERULE_MAX_WAIT_TIME (default 50s)
  --dump-bush <path>         Download HDF5 bush file to <path> (must end with .h5)
  --dump-skill               Print HDF5 bush reader skill (format docs + examples)
  KEY=value                  Set arbitrary environment variable
  --help                     Show this message
`);
}

function parseArgs(argv: string[]): CliOptions | null {
  let token = process.env.CODERULE_TOKEN;
  let rootPath: string | undefined;
  let mode: RunMode = 'service';
  let clean = false;
  let inlineHasher = false;
  let dumpBush: string | undefined;
  let dumpSkill = false;
  const env: Record<string, string> = {};

  const args = [...argv];
  while (args.length > 0) {
    const arg = args.shift()!;
    if (arg === '--help' || arg === '-h') {
      printUsage();
      return null;
    }

    if (arg === '--initial' || arg === '--once') {
      mode = 'initial';
      continue;
    }
    if (arg === '--service') {
      mode = 'service';
      continue;
    }
    if (arg === '--clean' || arg === '--reindex') {
      clean = true;
      continue;
    }
    if (arg === '--inline-hasher') {
      inlineHasher = true;
      continue;
    }
    if (arg === '--dump-bush') {
      if (args.length === 0) {
        throw new Error('Option --dump-bush requires a file path');
      }
      dumpBush = args.shift()!;
      if (!dumpBush.endsWith('.h5')) {
        throw new Error('--dump-bush path must end with .h5');
      }
      mode = 'initial';
      continue;
    }
    if (arg === '--dump-skill') {
      dumpSkill = true;
      mode = 'initial';
      continue;
    }

    // Handle --root separately (not as env var)
    if (arg === '--root') {
      if (args.length === 0) {
        throw new Error('Option --root requires a value');
      }
      rootPath = args.shift()!;
      continue;
    }

    if (arg.startsWith('--')) {
      const flag = arg.slice(2);
      const envKey = ENV_FLAG_MAP[flag];
      if (!envKey) {
        throw new Error(`Unknown option: ${arg}`);
      }
      if (args.length === 0) {
        throw new Error(`Option ${arg} requires a value`);
      }
      const value = args.shift()!;
      env[envKey] = value;
      continue;
    }

    if (arg.includes('=')) {
      const [key, value] = arg.split('=', 2);
      if (!key || value === undefined) {
        throw new Error(`Invalid KEY=value argument: ${arg}`);
      }
      env[key] = value;
      continue;
    }

    if (!token) {
      token = arg;
      continue;
    }

    throw new Error(`Unexpected argument: ${arg}`);
  }

  if (!token) {
    throw new Error(
      'Missing token. Provide via argument or CODERULE_TOKEN env.',
    );
  }

  return {
    token,
    rootPath,
    mode,
    clean,
    inlineHasher,
    dumpBush,
    dumpSkill,
    env,
  };
}

async function ensureClean(
  configToken: string,
  rootPath?: string,
): Promise<void> {
  const config = await resolveConfig({ token: configToken, rootPath });
  const targets = [
    config.dbPath,
    `${config.dbPath}-shm`,
    `${config.dbPath}-wal`,
  ];
  await Promise.all(targets.map((target) => fs.rm(target, { force: true })));
  await fs
    .rm(path.join(config.dataDir, 'watch', `${config.rootId}.sqlite-shm`), {
      force: true,
    })
    .catch(() => {});
  const dir = path.dirname(config.dbPath);
  await fs.mkdir(dir, { recursive: true });
  console.log(`Removed scanner database at ${config.dbPath}`);
}

async function main(): Promise<void> {
  try {
    const options = parseArgs(process.argv.slice(2));
    if (!options) {
      return;
    }

    process.env.CODERULE_TOKEN = options.token;
    if (options.inlineHasher) {
      process.env.CODERULE_HASHER_INLINE = '1';
    }
    for (const [key, value] of Object.entries(options.env)) {
      process.env[key] = value;
    }

    if (options.clean) {
      await ensureClean(options.token, options.rootPath);
    }

    if (options.mode === 'initial') {
      const result = await runInitialSync({
        token: options.token,
        rootPath: options.rootPath,
      });
      console.log('Initial sync result:', JSON.stringify(result, null, 2));

      if (options.dumpBush || options.dumpSkill) {
        const clients = new CoderuleClients({ token: options.token });
        try {
          // Trigger auth to discover sync server_url
          await clients.sync.checkSnapshotStatus(result.snapshotHash);

          if (options.dumpBush) {
            const buf = await clients.sync.downloadSnapshotBush(
              result.snapshotHash,
            );
            const dir = path.dirname(options.dumpBush);
            await fs.mkdir(dir, { recursive: true });
            await fs.writeFile(options.dumpBush, Buffer.from(buf));
            console.log(
              `HDF5 bush saved to ${options.dumpBush} (${buf.byteLength} bytes)`,
            );
          }
          if (options.dumpSkill) {
            const skill = await clients.sync.getHdf5BushReaderSkill();
            console.log(skill);
          }
        } finally {
          clients.close();
        }
      }
    } else {
      await runService({ token: options.token, rootPath: options.rootPath });
    }
  } catch (error) {
    console.error('Scanner CLI failed:', error);
    process.exitCode = 1;
  }
}

void main();
